import type { Buffer } from "../model/buffer";
import { createBuffer, lineLength } from "../model/buffer";
import type { Cursor } from "../model/cursor";
import type { Range } from "../model/range";

export interface NormalizedRange {
  readonly from: Cursor;
  readonly to: Cursor;
}

function comparePositions(a: Cursor, b: Cursor): number {
  return a.line - b.line || a.col - b.col;
}

/** Rangeのstart/endを文字順（from <= to）に正規化する。モーションの向きに依存しない。 */
export function normalizeRange(range: Range): NormalizedRange {
  return comparePositions(range.start, range.end) <= 0
    ? { from: range.start, to: range.end }
    : { from: range.end, to: range.start };
}

/** inclusive Range の終端を、exclusive相当（1文字分含める）の境界へ変換する。 */
export function toExclusiveEnd(buffer: Buffer, pos: Cursor): Cursor {
  return { line: pos.line, col: Math.min(pos.col + 1, lineLength(buffer, pos.line)) };
}

export interface CharDeletionResult {
  readonly buffer: Buffer;
  readonly deletedLines: readonly string[];
}

/** [from, to) の文字範囲（複数行にまたがってよい）を削除する。 */
export function deleteCharRangeExclusive(
  buffer: Buffer,
  from: Cursor,
  to: Cursor,
): CharDeletionResult {
  const lines = [...buffer.lines];

  if (from.line === to.line) {
    const line = lines[from.line] ?? "";
    const deleted = line.slice(from.col, to.col);
    lines[from.line] = line.slice(0, from.col) + line.slice(to.col);
    return { buffer: createBuffer(lines), deletedLines: [deleted] };
  }

  const firstLine = lines[from.line] ?? "";
  const lastLine = lines[to.line] ?? "";
  const deletedLines = [
    firstLine.slice(from.col),
    ...lines.slice(from.line + 1, to.line),
    lastLine.slice(0, to.col),
  ];
  const merged = firstLine.slice(0, from.col) + lastLine.slice(to.col);
  const newLines = [...lines.slice(0, from.line), merged, ...lines.slice(to.line + 1)];
  return { buffer: createBuffer(newLines), deletedLines };
}

/** [from, to) の文字範囲を、バッファを変更せずに取り出す（ヤンク用）。 */
export function extractCharRange(buffer: Buffer, from: Cursor, to: Cursor): readonly string[] {
  if (from.line === to.line) {
    const line = buffer.lines[from.line] ?? "";
    return [line.slice(from.col, to.col)];
  }
  const firstLine = buffer.lines[from.line] ?? "";
  const lastLine = buffer.lines[to.line] ?? "";
  return [
    firstLine.slice(from.col),
    ...buffer.lines.slice(from.line + 1, to.line),
    lastLine.slice(0, to.col),
  ];
}

export interface LineDeletionResult {
  readonly buffer: Buffer;
  readonly deletedLines: readonly string[];
}

/** [fromLine, toLine] の行を丸ごと削除する（linewise）。 */
export function deleteLineRange(
  buffer: Buffer,
  fromLine: number,
  toLine: number,
): LineDeletionResult {
  const deletedLines = buffer.lines.slice(fromLine, toLine + 1);
  const remaining = [...buffer.lines.slice(0, fromLine), ...buffer.lines.slice(toLine + 1)];
  return { buffer: createBuffer(remaining), deletedLines };
}

export interface BlockRect {
  readonly fromLine: number;
  readonly toLine: number;
  readonly fromCol: number;
  /** 矩形の右端（この列を含む）。 */
  readonly toCol: number;
}

/** 起点とカーソルから矩形（行の範囲 × 列の範囲）を作る。向きに依存しない。 */
export function toBlockRect(anchor: Cursor, cursor: Cursor): BlockRect {
  return {
    fromLine: Math.min(anchor.line, cursor.line),
    toLine: Math.max(anchor.line, cursor.line),
    fromCol: Math.min(anchor.col, cursor.col),
    toCol: Math.max(anchor.col, cursor.col),
  };
}

/** 矩形の各行から、その列範囲の文字を取り出す（行が短ければ、あるぶんだけ）。 */
export function extractBlock(buffer: Buffer, rect: BlockRect): readonly string[] {
  const extracted: string[] = [];
  for (let line = rect.fromLine; line <= rect.toLine; line += 1) {
    const text = buffer.lines[line] ?? "";
    extracted.push(text.slice(rect.fromCol, rect.toCol + 1));
  }
  return extracted;
}

/** 矩形の各行から、その列範囲の文字だけを削除する（行そのものは消さない）。 */
export function deleteBlock(buffer: Buffer, rect: BlockRect): CharDeletionResult {
  const lines = [...buffer.lines];
  const deletedLines: string[] = [];
  for (let line = rect.fromLine; line <= rect.toLine; line += 1) {
    const text = lines[line] ?? "";
    deletedLines.push(text.slice(rect.fromCol, rect.toCol + 1));
    lines[line] = text.slice(0, rect.fromCol) + text.slice(rect.toCol + 1);
  }
  return { buffer: createBuffer(lines), deletedLines };
}
